import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Home as HomeIcon, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const PROPERTY_TYPES = {
  commercial: ["Office", "Medical / Dental Office", "Salon / Spa", "Preschool / Daycare", "Retail Space", "Other Commercial"],
  residential: ["House", "Apartment", "Condo / Townhome", "Other Residential"],
};
const SQFT = ["Under 1,000 sq ft", "1,000 - 2,000 sq ft", "2,000 - 3,500 sq ft", "3,500 - 5,000 sq ft", "5,000+ sq ft"];
const FREQ = ["One-time", "Weekly", "Bi-weekly", "Monthly", "Custom schedule"];

const inputCls =
  "w-full rounded-xl border border-forest/15 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-leaf/50 focus:border-leaf transition";

const Field = ({ label, required, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">
      {label} {required && <span className="text-leaf-hover">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600" role="alert">{error}</p>}
  </div>
);

export const QuoteForm = ({ defaultService = "", dark = false }) => {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", service_type: defaultService,
    property_type: "", zip_code: "", square_footage: "", frequency: "",
    start_date: "", notes: "", website: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const startedAt = useRef(Date.now());

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "service_type" ? { property_type: "" } : {}) }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = "Please enter your full name.";
    if (form.phone.trim().length < 7) er.phone = "Please enter a valid phone number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!form.service_type) er.service_type = "Please choose commercial or residential.";
    if (form.zip_code.trim().length < 3) er.zip_code = "Please enter your ZIP code or service area.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, started_at: startedAt.current });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl bg-white p-8 lg:p-12 text-center shadow-xl" data-testid="quote-success">
        <CheckCircle2 className="w-14 h-14 text-leaf mx-auto" />
        <h3 className="mt-5 font-display text-2xl font-bold text-forest">Request received.</h3>
        <p className="mt-3 text-slate-600 max-w-md mx-auto leading-relaxed">
          Thank you, {form.full_name.split(" ")[0]}. Your quote request has been saved and we will reach out shortly to talk through your space and build your custom estimate.
        </p>
        <p className="mt-4 text-sm text-slate-500">In the meantime, feel free to browse our <Link to="/blog" className="text-leaf-hover font-semibold underline underline-offset-2">cleaning guides</Link>.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className={`rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xl ${dark ? "bg-white" : "bg-white border border-forest/10"}`} data-testid="quote-form">
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute opacity-0 pointer-events-none h-0 w-0"
        style={{ position: "absolute", left: "-9999px" }}
      />
      <div className="grid grid-cols-2 gap-3 mb-6" role="radiogroup" aria-label="Service type">
        {[
          { v: "residential", label: "Residential", icon: HomeIcon, testid: "quote-service-residential" },
          { v: "commercial", label: "Commercial", icon: Building2, testid: "quote-service-commercial" },
        ].map(({ v, label, icon: Icon, testid }) => (
          <button
            key={v}
            type="button"
            data-testid={testid}
            onClick={() => set("service_type")({ target: { value: v } })}
            aria-pressed={form.service_type === v}
            className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3.5 font-display font-bold text-sm transition-all ${
              form.service_type === v
                ? "border-leaf bg-mint text-forest"
                : "border-forest/15 bg-white text-slate-500 hover:border-leaf/50"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {errors.service_type && <p className="mb-4 -mt-3 text-xs text-red-600" role="alert">{errors.service_type}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full name" required error={errors.full_name} htmlFor="q-name">
          <input id="q-name" data-testid="quote-name-input" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Miller" autoComplete="name" />
        </Field>
        <Field label="Phone" required error={errors.phone} htmlFor="q-phone">
          <input id="q-phone" data-testid="quote-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(314) 555-0100" autoComplete="tel" />
        </Field>
        <Field label="Email" required error={errors.email} htmlFor="q-email">
          <input id="q-email" data-testid="quote-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@email.com" autoComplete="email" />
        </Field>
        <Field label="ZIP code / service address" required error={errors.zip_code} htmlFor="q-zip">
          <input id="q-zip" data-testid="quote-zip-input" className={inputCls} value={form.zip_code} onChange={set("zip_code")} placeholder="63017" autoComplete="postal-code" />
        </Field>
        <Field label="Property type" htmlFor="q-ptype">
          <select id="q-ptype" data-testid="quote-property-type-select" className={inputCls} value={form.property_type} onChange={set("property_type")}>
            <option value="">Select...</option>
            {(PROPERTY_TYPES[form.service_type] || [...PROPERTY_TYPES.residential, ...PROPERTY_TYPES.commercial]).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>
        <Field label="Approx. square footage" htmlFor="q-sqft">
          <select id="q-sqft" data-testid="quote-sqft-select" className={inputCls} value={form.square_footage} onChange={set("square_footage")}>
            <option value="">Select...</option>
            {SQFT.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Cleaning frequency" htmlFor="q-freq">
          <select id="q-freq" data-testid="quote-frequency-select" className={inputCls} value={form.frequency} onChange={set("frequency")}>
            <option value="">Select...</option>
            {FREQ.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Desired start date" htmlFor="q-date">
          <input id="q-date" data-testid="quote-start-date-input" type="date" className={inputCls} value={form.start_date} onChange={set("start_date")} />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Additional notes" htmlFor="q-notes">
          <textarea id="q-notes" data-testid="quote-notes-input" rows={4} className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Pets, access instructions, priorities - anything that helps us tailor your quote." />
        </Field>
      </div>
      <button
        type="submit"
        data-testid="quote-submit-button"
        disabled={submitting}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 font-display font-bold text-white transition-all hover:bg-forest-soft hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitting ? "Sending..." : "Request My Free Quote"}
      </button>
      <p className="mt-3 text-center text-xs text-slate-500">No obligation. No hidden fees. We reply to every request personally.</p>
    </form>
  );
};
